import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plane, Clock, Sun, Briefcase, Heart, FileText, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { getExtras } from "../i18n/extraTranslations";
import { MountainLine } from "../components/MountainLine";
import { Reveal } from "../components/Reveal";
import { PillarBadges } from "../components/PillarBadges";
import { CoverageMap } from "../components/CoverageMap";
import { TID } from "../lib/testIds";

const Home = () => {
  const { t, lang } = useLang();
  const ex = getExtras(lang);

  const serviceIcons = { airport: Plane, hourly: Clock, fullday: Sun, business: Briefcase, events: Heart, courier: FileText };

  // Every service tile links into the single reservation funnel or the
  // consolidated /servicos page — no legacy standalone routes.
  const serviceRoutes = {
    airport: "/reservar?service=transfer",
    hourly: "/reservar?service=by_hour",
    fullday: "/reservar?service=full_day",
    business: "/contacto",
    events: "/reservar?service=transfer",
    courier: "/servicos",
  };

  return (
    <div>
      {/* HERO — cinematic video (BYD driving through Switzerland) */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/media/hero-poster.jpg"
            style={{ filter: "brightness(0.55) contrast(1.05)" }}
            data-testid="hero-video"
          >
            <source src="/videos/byd-driving.mp4" type="video/mp4" />
            <source src="/media/hero-1080.mp4" type="video/mp4" />
            <source src="/media/hero-720.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/40 to-brand-bg/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/70 via-transparent to-transparent" />
        </div>
        <MountainLine className="absolute bottom-0 left-0 right-0 w-full h-[10vh] pointer-events-none" opacity={0.4} />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 pb-24 w-full">
          <div className="max-w-2xl">
            <div className="mb-8 animate-fadeUp">
              <img
                src="/media/logo.png"
                alt="Swiss AlpenLine"
                className="h-40 md:h-56 lg:h-64 w-auto object-contain"
              />
            </div>
            <h1 className="display text-5xl sm:text-6xl lg:text-7xl text-brand-platinum leading-[1.05] animate-fadeUp" style={{ animationDelay: "150ms" }}>
              {t.hero.tagline}
            </h1>
            <p className="mt-8 text-brand-silver text-lg max-w-xl leading-relaxed animate-fadeUp" style={{ animationDelay: "300ms" }}>
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fadeUp" style={{ animationDelay: "450ms" }}>
              <Link to="/reservar" className="btn-pill btn-pill--solid" data-testid={TID.hero.ctaReserve}>
                {t.hero.ctaReserve} <ArrowRight size={14} />
              </Link>
              <a href="#calc" className="btn-pill" data-testid={TID.hero.ctaCalc}>
                {t.hero.ctaCalc}
              </a>
              <div className="hidden md:flex items-center gap-2 pl-4 ml-4 border-l border-brand-border text-brand-silver">
                <ShieldCheck size={14} className="text-brand-platinum" />
                <span className="text-[10px] tracking-widest2 uppercase">{ex.swiss_law_badge}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID (6 services) */}
      <section className="relative py-24 lg:py-32 px-6 ">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">— {t.services.title}</p>
              <h2 className="display text-4xl lg:text-5xl text-brand-platinum">A private line, six ways.</h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {Object.entries(ex.services_ext).map(([key, svc], i) => {
              const Icon = serviceIcons[key];
              const to = serviceRoutes[key] || "/servicos";
              return (
                <Reveal key={key} delay={i * 60}>
                  <Link to={to} className="group block h-full p-8 lg:p-10 transition-colors duration-500 hover:bg-brand-surface/60">
                    <div className="flex items-center justify-between mb-8">
                      <Icon size={22} className="text-brand-platinum" />
                      <span className="text-[10px] tracking-widest2 uppercase text-brand-dim">0{i + 1}</span>
                    </div>
                    <h3 className="display text-2xl text-brand-platinum mb-3">{svc.title}</h3>
                    <p className="text-sm text-brand-silver leading-relaxed">{svc.body}</p>
                    <span className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-widest2 uppercase text-brand-silver group-hover:text-brand-platinum transition-colors">
                      {t.common.moreInfo} <ArrowRight size={12} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA replacing Instant Fare — sales-focused, address-based booking pitch */}
      <section id="calc" className="relative py-24 lg:py-32 px-6 ">
        <div className="max-w-[1200px] mx-auto text-center">
          <Reveal>
            <p className="text-[10px] tracking-widest2 uppercase text-bronze mb-4">— Priced for your route, not for a table</p>
            <h2 className="display text-4xl lg:text-5xl text-brand-platinum max-w-3xl mx-auto">
              Same comfort. Same discretion.<br/><span className="text-brand-silver italic">A simpler price.</span>
            </h2>
            <p className="mt-6 text-brand-silver max-w-xl mx-auto">
              Tell us where to pick you up and where to drop you off. In seconds you get an exclusive price — calculated for your trajectory only, never from a menu.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/reservar" className="btn-pill btn-pill--solid">Reserve now <ArrowRight size={14} /></Link>
              <Link to="/tarifario" className="btn-pill">See how prices work</Link>
            </div>
            <PillarBadges align="center" className="mt-10" />
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS (3 steps) */}
      <section className="relative py-24 lg:py-32 px-6 ">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">— 03</p>
              <h2 className="display text-4xl lg:text-5xl text-brand-platinum">{ex.how.title}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ex.how.steps.map((s, i) => (
              <Reveal key={s.k} delay={i * 100}>
                <div className="border border-brand-border p-8 lg:p-10 h-full">
                  <p className="display text-6xl text-brand-platinum/40 mb-6">{s.k}</p>
                  <h3 className="display text-2xl text-brand-platinum mb-3">{s.t}</h3>
                  <p className="text-sm text-brand-silver leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE — full-bleed, no frame, seamless with the page */}
      <section className="relative py-24 overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-6">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">— 04</p>
              <h2 className="display text-4xl lg:text-5xl text-brand-platinum">{ex.coverage.title}</h2>
              <p className="mt-4 text-brand-silver max-w-lg">{ex.coverage.subtitle}</p>
            </div>
          </Reveal>
        </div>
        <Reveal>
          <CoverageMap />
        </Reveal>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 lg:py-32 px-6 ">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">— 05</p>
              <h2 className="display text-4xl lg:text-5xl text-brand-platinum">{ex.testimonials.title}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ex.testimonials.items.map((it, i) => (
              <Reveal key={it.a || i} delay={i * 100}>
                <blockquote className="border border-brand-border p-8 h-full">
                  <p className="display text-xl text-brand-platinum leading-relaxed italic">&ldquo;{it.q}&rdquo;</p>
                  <div className="hairline my-6" />
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">— {it.a}</p>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHAUFFEUR — quietly worded profile, no photograph */}
      <section className="relative py-24 lg:py-28 px-6">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-6">— {ex.chauffeur.kicker}</p>
            <h2 className="display text-4xl lg:text-5xl text-brand-platinum leading-[1.05] max-w-3xl">
              {ex.chauffeur.heading}
            </h2>
            <p className="mt-8 text-brand-silver leading-relaxed max-w-2xl">{ex.chauffeur.body}</p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl">
              {ex.chauffeur.pillars.map((p, i) => (
                <div key={p} className="flex items-start gap-4">
                  <span className="display text-3xl text-bronze tabular-nums leading-none">0{i + 1}</span>
                  <span className="text-sm text-brand-platinum leading-snug tracking-wide">{p}</span>
                </div>
              ))}
            </div>

            <p className="mt-12 text-xs tracking-wide italic text-brand-silver max-w-xl">— {ex.chauffeur.note}</p>
          </Reveal>
        </div>
      </section>

      {/* CORPORATE ACCOUNTS — full-bleed background, blended into page */}
      <section className="relative overflow-hidden">
        {/* Background photograph */}
        <div className="absolute inset-0">
          <img
            src="/images/byd-burgenstock.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.35) contrast(1.05)" }}
          />
          {/* Seamless blend with page bg */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-bg via-brand-bg/40 to-brand-bg" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/85 via-brand-bg/30 to-transparent" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-32 lg:py-40">
          <Reveal>
            <p className="text-[11px] tracking-widest2 uppercase mb-6" style={{ color: "#B08D2F" }}>
              — {ex.business.title}
            </p>
            <h2 className="display text-4xl md:text-5xl lg:text-6xl text-brand-platinum leading-[1.05] max-w-3xl">
              {ex.business.heading}
            </h2>
            <p className="mt-8 text-brand-silver leading-relaxed max-w-xl">{ex.business.body}</p>

            {/* Three pillars — no boxes, just clean type */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
              {ex.business.pillars.map((p, i) => (
                <div key={p} className="flex items-start gap-3">
                  <span className="display text-2xl text-bronze tabular-nums leading-none pt-0.5">0{i + 1}</span>
                  <span className="text-sm tracking-wide text-brand-platinum leading-snug">{p}</span>
                </div>
              ))}
            </div>

            <Link to="/contacto" className="btn-pill mt-12" data-testid="corp-cta-home">
              {ex.business.cta} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INCLUSION CLOSER */}
      <section className="relative py-20 px-6 ">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-6">— Woman-safe · Inclusive</p>
            <p className="wordmark text-4xl lg:text-5xl text-brand-platinum leading-tight">
              A journey without noise. A cabin without judgment.
            </p>
            <p className="mt-6 text-sm text-brand-silver max-w-xl mx-auto leading-relaxed">
              All faiths, ethnicities, orientations and identities are equally welcome on board.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
